import React, { useEffect, useCallback, useRef } from 'react';
import './index.less';
import triangle from '@/assets/finance/welcome/triangle.png';
import useIntersectionObserver from '../../../../../../hooks/useIntersectionObserver';

interface ItrainingAnimationItem {
  icon?: string;
  title: string;
  content: any[];
}

interface ItrainingAnimationProps {
  items: ItrainingAnimationItem[];
  style?: React.CSSProperties;
  title?: string | null;
  onCompleted?: () => void;
}

const TrainingAnimation: React.FunctionComponent<ItrainingAnimationProps> = ({
  title,
  items,
  style,
  onCompleted,
}) => {
  const [play, setPlay] = React.useState(1);
  const timeoutRef = useRef<any>(null);
  const isMountedRef = useRef(true);
  const hasStartedRef = useRef(false);
  const startPlayRef = useRef<(next?: number) => void>();
  const startPlay = useCallback(
    (next = 1) => {
      if (!isMountedRef.current) {
        return;
      }

      setPlay(next);
      next += 1;
      if (next <= items.length) {
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && startPlayRef.current) {
            startPlayRef.current(next);
          }
        }, 1200);
      } else {
        if (isMountedRef.current) {
          onCompleted && onCompleted();
        }
      }
    },
    [items.length, onCompleted]
  );

  // 更新 ref 中的函数引用
  startPlayRef.current = startPlay;

  const { targetRef, isVisible } = useIntersectionObserver({
    threshold: 0.5, // 元素 30% 进入视口时触发
    rootMargin: '-50px', // 根边距缩小 50px（需元素进入视口 50px 后才判定）
  });
  useEffect(() => {
    if (isVisible) {
      // 如果已经播放过，先清除之前的定时器并重置
      if (hasStartedRef.current) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setPlay(1);
      }
      // 开始播放动画
      hasStartedRef.current = true;
      if (startPlayRef.current) {
        startPlayRef.current();
      }
    } else {
      // 当不可见时，清除定时器并重置状态
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      hasStartedRef.current = false;
      setPlay(1);
    }
    return () => {
      // 清理函数：清除定时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isVisible]); // 移除 startPlay 依赖，使用 ref 调用

  // 清理函数：组件卸载时标记为未挂载
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);
  return (
    <div ref={targetRef} className="training-animation" style={style}>
      <div className="training-animation-list">
        {items?.map((itm: ItrainingAnimationItem, idx: number) => (
          <div
            key={idx}
            className={`training-animation-item  animation ${
              play >= idx + 1 ? 'fade-in' : ''
            }`}
          >
            <div className="training-animation-icon">
              <img className="triangle" src={triangle} alt="triangle" />
              <div
                className="content-left"
                dangerouslySetInnerHTML={{ __html: itm.title }}
              />
            </div>
            <div className="training-animation-content">
              <ul>
                {itm?.content?.map((itms: string, idxs: number) => (
                  <li key={idxs} dangerouslySetInnerHTML={{ __html: itms }} />
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

TrainingAnimation.defaultProps = {
  style: {},
  title: null,
};

export default TrainingAnimation;
